import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import type { TimePeriod, TeamTab, SalesMetrics, RepairMetrics, InstallationMetrics, TeamAggregated } from './types';
import { getSalesMetrics, getRepairMetrics, getInstallationMetrics, getSession, AUTH_REQUIRED_EVENT } from './services/api';
import type { SessionState } from './services/api';
import { EmployeeCard } from './components/EmployeeCard';
import { SummaryStats } from './components/SummaryStats';
import { Filters } from './components/Filters';
import { TeamManagement } from './components/TeamManagement';
import { PinModal, clearLegacySession } from './components/PinModal';

// ---- Server-side session (httpOnly cookie) --------------------------------
interface AuthContextValue {
  session: SessionState;
  refresh: () => Promise<SessionState | null>;
}
const AuthContext = createContext<AuthContextValue | null>(null);

function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthGate');
  return ctx;
}

// Nothing (no data request) renders until the server confirms a valid session.
function AuthGate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionState | null>(null);
  const [unreachable, setUnreachable] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await getSession();
      setSession(s);
      setUnreachable(false);
      return s;
    } catch {
      setUnreachable(true);
      return null;
    }
  }, []);

  useEffect(() => {
    clearLegacySession();
    refresh();
  }, [refresh]);

  // Any API 401 (expired or revoked session) -> re-check and prompt again.
  useEffect(() => {
    const onAuthRequired = () => { refresh(); };
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
  }, [refresh]);

  if (!session) {
    return (
      <div className="container">
        <div className="loading">
          {unreachable ? (
            <>
              <div>Cannot reach the server.</div>
              <button className="pin-submit" onClick={() => refresh()}>Retry</button>
            </>
          ) : (
            <>
              <div className="spinner"></div>
              <div>Loading...</div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!session.authenticated) {
    return (
      <div className="container">
        <header>
          <div className="logo-text">Roman's Service - Team Dashboard</div>
        </header>
        <PinModal
          title="Enter PIN"
          description={session.configured
            ? 'Enter your PIN to open the dashboard'
            : 'Dashboard access is not configured yet. Please contact support.'}
          onSuccess={() => { refresh(); }}
        />
      </div>
    );
  }

  return <AuthContext.Provider value={{ session, refresh }}>{children}</AuthContext.Provider>;
}

// Team-only view (for /sales, /repair, /installation links)
function TeamView() {
  const location = useLocation();
  const [period, setPeriod] = useState<TimePeriod>('year');
  const [customDates, setCustomDates] = useState<{ start?: string; end?: string }>({});
  const [sortBy, setSortBy] = useState<string>('estimatesWonAmount');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [aggregated, setAggregated] = useState<TeamAggregated | null>(null);

  // Extract team from path: /sales -> sales, /repair -> repair, etc.
  const teamType = location.pathname.slice(1) as TeamTab;
  const teamName = teamType === 'sales' ? 'Sales Team' :
                   teamType === 'repair' ? 'Repair Technicians' :
                   'Installation Team';

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  useEffect(() => {
    if (teamType === 'sales') setSortBy('estimatesWonAmount');
    else if (teamType === 'repair') setSortBy('estimatesWonAmount');
    else setSortBy('totalInstallationJobs');
  }, [teamType]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const startDate = period === 'custom' ? customDates.start : undefined;
        const endDate = period === 'custom' ? customDates.end : undefined;

        if (teamType === 'sales') {
          const result = await getSalesMetrics(period, startDate, endDate);
          setData(result.employees);
          setAggregated(result.aggregated);
        } else if (teamType === 'repair') {
          const result = await getRepairMetrics(period, startDate, endDate);
          setData(result.employees);
          setAggregated(result.aggregated);
        } else {
          const result = await getInstallationMetrics(period, startDate, endDate);
          setData(result.employees);
          setAggregated(result.aggregated);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (period === 'custom' && (!customDates.start || !customDates.end)) {
      return;
    }

    fetchData();
  }, [teamType, period, customDates]);

  const getSortOptions = () => {
    if (teamType === 'sales') {
      return [
        { value: 'estimatesWonAmount', label: 'Won Amount' },
        { value: 'estimatesWon', label: 'Won Count' },
        { value: 'conversionRate', label: 'Conversion Rate' },
        { value: 'avgTicket', label: 'Avg Ticket' }
      ];
    } else if (teamType === 'repair') {
      return [
        { value: 'estimatesWonAmount', label: 'Won Amount' },
        { value: 'serviceCalls', label: 'Service Calls' },
        { value: 'serviceCallsRevenue', label: 'Service Revenue' },
        { value: 'callbacks', label: 'Callbacks' },
        { value: 'conversionRate', label: 'Conversion Rate' }
      ];
    } else {
      return [
        { value: 'totalInstallationJobs', label: 'Total Jobs' },
        { value: 'callbacks', label: 'Callbacks' },
        { value: 'failedInspections', label: 'Failed Inspections' }
      ];
    }
  };

  const getSortedData = () => {
    return [...data].sort((a, b) => {
      if (sortBy === 'callbacks' || sortBy === 'failedInspections') {
        return a[sortBy] - b[sortBy];
      }
      return b[sortBy] - a[sortBy];
    });
  };

  const handlePeriodChange = (newPeriod: TimePeriod, startDate?: string, endDate?: string) => {
    setPeriod(newPeriod);
    if (newPeriod === 'custom' && startDate && endDate) {
      setCustomDates({ start: startDate, end: endDate });
    } else if (newPeriod !== 'custom') {
      setCustomDates({});
    }
  };

  return (
    <div className="container">
      <header>
        <div className="logo-text">Roman's Service - {teamName}</div>
        <div className="date-display">{currentDate}</div>
      </header>

      <div className="dashboard-section">
        <div className="stats-filters-row">
          <SummaryStats activeTab={teamType} aggregated={aggregated} />
          <Filters
            period={period}
            onPeriodChange={handlePeriodChange}
            sortBy={sortBy}
            onSortChange={setSortBy}
            sortOptions={getSortOptions()}
            customStartDate={customDates.start}
            customEndDate={customDates.end}
          />
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <div>Loading {teamType} data...</div>
          </div>
        ) : getSortedData().length === 0 ? (
          <div className="empty-state">
            <div>No data available for this period</div>
            <div className="empty-hint">Try selecting a different time period</div>
          </div>
        ) : (
          <div className="employee-grid">
            {getSortedData().map((employee, index) => (
              <EmployeeCard
                key={employee.employeeId}
                employee={employee}
                rank={index + 1}
                type={teamType}
              />
            ))}
          </div>
        )}
      </div>

      <footer>
        <div>&copy; {new Date().getFullYear()} Roman's Service - {teamName}</div>
      </footer>
    </div>
  );
}

// Full dashboard with tabs
function FullDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TeamTab>('sales');
  const [period, setPeriod] = useState<TimePeriod>('year');
  const [customDates, setCustomDates] = useState<{ start?: string; end?: string }>({});
  const [sortBy, setSortBy] = useState<string>('estimatesWonAmount');
  const [loading, setLoading] = useState(true);

  // Settings needs a PIN entered within the last 24h (server decides: session.settingsUnlocked)
  const { session, refresh } = useAuth();
  const settingsUnlocked = session.settingsUnlocked;
  const [showPinModal, setShowPinModal] = useState(false);

  // Data states
  const [salesData, setSalesData] = useState<SalesMetrics[]>([]);
  const [repairData, setRepairData] = useState<RepairMetrics[]>([]);
  const [installData, setInstallData] = useState<InstallationMetrics[]>([]);
  const [aggregated, setAggregated] = useState<TeamAggregated | null>(null);

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Check URL for settings tab
  useEffect(() => {
    if (location.pathname === '/settings') {
      if (!settingsUnlocked) {
        setShowPinModal(true);
      } else {
        setActiveTab('settings');
      }
    }
  }, [location.pathname, settingsUnlocked]);

  // Settings open but the 24h PIN window ran out (server answered 401) -> ask again
  useEffect(() => {
    if (activeTab === 'settings' && !settingsUnlocked) {
      setShowPinModal(true);
    }
  }, [activeTab, settingsUnlocked]);

  // Fetch data based on active tab and period
  useEffect(() => {
    if (activeTab === 'settings') return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const startDate = period === 'custom' ? customDates.start : undefined;
        const endDate = period === 'custom' ? customDates.end : undefined;

        if (activeTab === 'sales') {
          const data = await getSalesMetrics(period, startDate, endDate);
          setSalesData(data.employees);
          setAggregated(data.aggregated);
        } else if (activeTab === 'repair') {
          const data = await getRepairMetrics(period, startDate, endDate);
          setRepairData(data.employees);
          setAggregated(data.aggregated);
        } else {
          const data = await getInstallationMetrics(period, startDate, endDate);
          setInstallData(data.employees);
          setAggregated(data.aggregated);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (period === 'custom' && (!customDates.start || !customDates.end)) {
      return;
    }

    fetchData();
  }, [activeTab, period, customDates]);

  const getSortOptions = () => {
    if (activeTab === 'sales') {
      return [
        { value: 'estimatesWonAmount', label: 'Won Amount' },
        { value: 'estimatesWon', label: 'Won Count' },
        { value: 'conversionRate', label: 'Conversion Rate' },
        { value: 'avgTicket', label: 'Avg Ticket' }
      ];
    } else if (activeTab === 'repair') {
      return [
        { value: 'estimatesWonAmount', label: 'Won Amount' },
        { value: 'serviceCalls', label: 'Service Calls' },
        { value: 'serviceCallsRevenue', label: 'Service Revenue' },
        { value: 'callbacks', label: 'Callbacks' },
        { value: 'conversionRate', label: 'Conversion Rate' }
      ];
    } else {
      return [
        { value: 'totalInstallationJobs', label: 'Total Jobs' },
        { value: 'callbacks', label: 'Callbacks' },
        { value: 'failedInspections', label: 'Failed Inspections' }
      ];
    }
  };

  const getSortedData = () => {
    let data: any[] = [];
    if (activeTab === 'sales') data = [...salesData];
    else if (activeTab === 'repair') data = [...repairData];
    else data = [...installData];

    return data.sort((a, b) => {
      if (sortBy === 'callbacks' || sortBy === 'failedInspections') {
        return a[sortBy] - b[sortBy];
      }
      return b[sortBy] - a[sortBy];
    });
  };

  const handleTabChange = (tab: TeamTab) => {
    if (tab === 'settings') {
      if (!settingsUnlocked) {
        setShowPinModal(true);
        return;
      }
      navigate('/settings');
    } else {
      if (location.pathname === '/settings') {
        navigate('/');
      }
    }

    setActiveTab(tab);
    if (tab === 'sales') setSortBy('estimatesWonAmount');
    else if (tab === 'repair') setSortBy('estimatesWonAmount');
    else if (tab === 'installation') setSortBy('totalInstallationJobs');
  };

  const handlePinSuccess = async () => {
    await refresh();
    setShowPinModal(false);
    setActiveTab('settings');
    navigate('/settings');
  };

  const handlePinCancel = () => {
    setShowPinModal(false);
    if (activeTab === 'settings' || location.pathname === '/settings') {
      setActiveTab('sales');
      setSortBy('estimatesWonAmount');
      navigate('/');
    }
  };

  const handlePeriodChange = (newPeriod: TimePeriod, startDate?: string, endDate?: string) => {
    setPeriod(newPeriod);
    if (newPeriod === 'custom' && startDate && endDate) {
      setCustomDates({ start: startDate, end: endDate });
    } else if (newPeriod !== 'custom') {
      setCustomDates({});
    }
  };

  return (
    <div className="container">
      <header>
        <div className="logo-text">Roman's Service - Team Dashboard</div>
        <div className="date-display">{currentDate}</div>
      </header>

      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'sales' ? 'active' : ''}`}
          onClick={() => handleTabChange('sales')}
        >
          Sales Team
        </button>
        <button
          className={`tab-button ${activeTab === 'repair' ? 'active' : ''}`}
          onClick={() => handleTabChange('repair')}
        >
          Repair Technicians
        </button>
        <button
          className={`tab-button ${activeTab === 'installation' ? 'active' : ''}`}
          onClick={() => handleTabChange('installation')}
        >
          Installation Team
        </button>
        <button
          className={`tab-button settings-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => handleTabChange('settings')}
        >
          Settings
        </button>
      </div>

      <div className="dashboard-section">
        {activeTab === 'settings' ? (
          <TeamManagement />
        ) : (
          <>
            <div className="stats-filters-row">
              <SummaryStats activeTab={activeTab} aggregated={aggregated} />
              <Filters
                period={period}
                onPeriodChange={handlePeriodChange}
                sortBy={sortBy}
                onSortChange={setSortBy}
                sortOptions={getSortOptions()}
                customStartDate={customDates.start}
                customEndDate={customDates.end}
              />
            </div>

            {loading ? (
              <div className="loading">
                <div className="spinner"></div>
                <div>Loading {activeTab} data...</div>
              </div>
            ) : getSortedData().length === 0 ? (
              <div className="empty-state">
                <div>No data available for this period</div>
                <div className="empty-hint">Try selecting a different time period or check team assignments</div>
              </div>
            ) : (
              <div className="employee-grid">
                {getSortedData().map((employee, index) => (
                  <EmployeeCard
                    key={employee.employeeId}
                    employee={employee}
                    rank={index + 1}
                    type={activeTab}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <footer>
        <div>&copy; {new Date().getFullYear()} Roman's Service - Team Performance Dashboard</div>
      </footer>

      {showPinModal && (
        <PinModal
          onSuccess={handlePinSuccess}
          onCancel={handlePinCancel}
        />
      )}
    </div>
  );
}

// Main App with routing
function App() {
  return (
    <AuthGate>
      <Routes>
        <Route path="/sales" element={<TeamView />} />
        <Route path="/repair" element={<TeamView />} />
        <Route path="/installation" element={<TeamView />} />
        <Route path="/settings" element={<FullDashboard />} />
        <Route path="/" element={<FullDashboard />} />
      </Routes>
    </AuthGate>
  );
}

export default App;
